import { useEffect, useRef, useState } from "react";

// Feature-detect Speech Recognition
const SR = typeof window !== "undefined" ? (window.SpeechRecognition || window.webkitSpeechRecognition) : null;

export const isSpeechRecognitionSupported = () => Boolean(SR);

/**
 * useSpeechRecorder — Web Speech API wrapper
 * Returns:
 *   supported, recording, transcript, interim, duration (sec)
 *   start(), stop(), reset()
 *   error
 */
export function useSpeechRecorder({ lang = "nl-NL" } = {}) {
  const [supported] = useState(isSpeechRecognitionSupported());
  const [recording, setRecording] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [interim, setInterim] = useState("");
  const [duration, setDuration] = useState(0);
  const [error, setError] = useState("");

  const recognitionRef = useRef(null);
  const startTimeRef = useRef(0);
  const timerRef = useRef(null);
  const finalRef = useRef("");
  const shouldRestartRef = useRef(false);
  const langRef = useRef(lang);
  // Keep the latest lang reachable inside long-lived handlers (onend closes over it via ref).
  langRef.current = lang;

  const startTimer = () => {
    startTimeRef.current = Date.now();
    timerRef.current = setInterval(() => {
      setDuration((Date.now() - startTimeRef.current) / 1000);
    }, 100);
  };

  const stopTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  /**
   * Force the recognizer to the requested language. Some Chrome versions silently reset
   * SpeechRecognition.lang to the browser/system default (often en-US) between sessions,
   * so we re-assert it before every start / restart. Never rely on the default.
   */
  const applyLang = (rec) => {
    const wanted = langRef.current || "nl-NL";
    try { rec.lang = wanted; } catch (_e) { /* readonly on some polyfills — ignore */ }
    // Defensive: recognizers occasionally read these lazily; set them explicitly.
    try { rec.continuous = true; } catch (_e) { /* ignore */ }
    try { rec.interimResults = true; } catch (_e) { /* ignore */ }
    return wanted;
  };

  const start = () => {
    if (!supported) {
      setError("Speech recognition is not supported in this browser. Try Chrome or Edge on desktop or Android.");
      return;
    }
    setError("");
    finalRef.current = "";
    setTranscript("");
    setInterim("");
    setDuration(0);

    const rec = new SR();
    const effectiveLang = applyLang(rec);
    // One-time debug line so we can confirm nl-NL is actually in force on user devices.
    console.info(`[TW1 Web Speech] effective lang = ${effectiveLang}`);

    rec.onresult = (event) => {
      let interimText = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const res = event.results[i];
        if (res.isFinal) {
          finalRef.current += res[0].transcript + " ";
        } else {
          interimText += res[0].transcript;
        }
      }
      setTranscript(finalRef.current.trim());
      setInterim(interimText);
    };

    rec.onerror = (event) => {
      if (event.error === "no-speech" || event.error === "aborted") return;
      setError(`Speech error: ${event.error}`);
    };

    rec.onend = () => {
      // Auto-restart if user hasn't stopped manually (some browsers cut off after ~60s of silence).
      // CRITICAL: re-assert lang before restart. Chrome resets it to en-US on some builds.
      if (shouldRestartRef.current) {
        applyLang(rec);
        try {
          rec.start();
        } catch (_e) {
          /* browser may throw on rapid restart — safe to ignore */
        }
      } else {
        setRecording(false);
        stopTimer();
      }
    };

    try {
      shouldRestartRef.current = true;
      rec.start();
      recognitionRef.current = rec;
      setRecording(true);
      startTimer();
    } catch (e) {
      setError("Could not start recording. Please grant microphone permission.");
    }
  };

  const stop = () => {
    shouldRestartRef.current = false;
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch (_e) { /* noop */ }
    }
    setRecording(false);
    stopTimer();
    setInterim("");
  };

  const reset = () => {
    stop();
    finalRef.current = "";
    setTranscript("");
    setInterim("");
    setDuration(0);
    setError("");
  };

  // Cleanup
  useEffect(() => {
    return () => {
      shouldRestartRef.current = false;
      if (recognitionRef.current) {
        try { recognitionRef.current.stop(); } catch (_e) { /* noop */ }
      }
      stopTimer();
    };
  }, []);

  return { supported, recording, transcript, interim, duration, error, start, stop, reset };
}

export function formatDuration(sec) {
  const s = Math.max(0, Math.floor(sec));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}`;
}
